import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Drop-in replacement for `window.confirm` that works inside the
 * Emergent preview iframe (where native browser dialogs are
 * suppressed). Controlled — caller owns the `open` state.
 *
 * Usage:
 *   const [pending, setPending] = useState(null);
 *   ...
 *   <ConfirmDialog
 *     open={!!pending}
 *     title="Remove from network?"
 *     description="You can re-invite them anytime."
 *     destructive
 *     onCancel={() => setPending(null)}
 *     onConfirm={async () => { await doThing(pending); setPending(null); }}
 *   />
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Yes, do it",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onCancel,
  onConfirm,
  testid = "confirm-dialog",
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel?.(); }}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription className="pt-1">{description}</DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testid}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className={destructive ? "bg-red-600 hover:bg-red-700 text-white" : "bg-ink hover:bg-black"}
            data-testid={`${testid}-confirm`}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
